import {
  Activity, Award, BarChart3, BookOpen, BookText, Calendar, CircleDollarSign, Clock, File, FileText, FlaskConical, FolderKanban,
  GraduationCap, Home, Inbox, KeyRound, Mail, ScrollText, TrendingUp, UserCog, UserPlus, Users,
} from "lucide-react";
import { canSeeLabel, runsCompany } from "../../Services/pageAccess.js";

// The menu, in one place: the sidebar, the page title in the top bar and the highlighted entry all come from this.
// An entry's label must stay byte-identical to LABEL_TO_PAGE in Services/pageAccess.js and nav_label in the backend
// page registry (test_nav_labels_match.py fails the build if they drift).
//   alert   names a flag in `flags` (useNavFlags): a red dot while it is on
//   urgent  names a flag: the whole row turns red while it is on
//   when    the section shows only if this returns true for `me`
const NAV_SECTIONS = [
  { label: null, items: [
    { label: "Home", icon: Home, path: "/home/" },
  ] },
  { label: "Sales", items: [
    { label: "Sales CRM", icon: BookOpen, path: "/lms/" },
    { label: "Sales CRM ✨", icon: BookOpen, path: "/crm/" },
    { label: "Sales Pipeline", icon: BarChart3, path: "/lms/analytics/" },
    { label: "SDR Onboarding", icon: GraduationCap, path: "/junior-onboarding/" },
    { label: "AE Onboarding", icon: GraduationCap, path: "/sales-onboarding/" },
    { label: "Playbook", icon: BookText, path: "/playbook/" },
    { label: "RevOps", icon: BarChart3, path: "/revops/" },
    { label: "RevOps V2", icon: BarChart3, path: "/revops-v2/" },
  ] },
  { label: "Performance & Goals", items: [
    { label: "Sales Performance", icon: Award, path: "/sales-performance/" },
    { label: "Growth Roadmap", icon: TrendingUp, path: "/growth-roadmap/" },
  ] },
  { label: "Delivery", items: [
    { label: "Projects", icon: FolderKanban, path: "/projects/" },
    { label: "Project Management", icon: FolderKanban, path: "/projects/management/" },
    { label: "Delay Management", icon: Clock, path: "/delays/", urgent: "activeDelay" },
  ] },
  { label: "Development", items: [
    { label: "DevOps", icon: KeyRound, path: "/devops/" },
    { label: "Vibe testing (Avalok)", icon: FlaskConical, path: "/vibe-testing/" },
    { label: "Monitoring", icon: Activity, path: "/monitoring/" },
    { label: "Logs", icon: ScrollText, path: "/logs/" },
  ] },
  { label: "People & HR", items: [
    { label: "HRMS", icon: Users, path: "/hrms/" },
    { label: "Leave Requests", icon: Calendar, path: "/leave-requests/" },
    { label: "Send Offer Letter", icon: UserPlus, path: "/Onboard/Send_Offer" },
    { label: "IDs Waiting", icon: UserCog, path: "/joining-reviews/", alert: "idsWaiting" },
    { label: "Send Certificate", icon: File, path: "/send-certificate" },
    { label: "Deactivate Employee", icon: UserCog, path: "/deactivate-multiple-employee/" },
  ] },
  { label: "Finance", items: [
    { label: "Finance", icon: CircleDollarSign, path: "/payments/" },
    { label: "P&L", icon: BarChart3, path: "/pnl/" },
    { label: "Employee Register", icon: UserCog, path: "/employee-register/" },
  ] },
  // Company-wide reference material, one entry per project, each its own access-controlled page.
  { label: "Documents", items: [
    { label: "How we work", icon: File, path: "/docs/company/" },
    { label: "Intranet", icon: File, path: "/docs/intranet/" },
    { label: "InterviewGod", icon: File, path: "/docs/interviewgod/" },
    { label: "Banao Technologies", icon: File, path: "/docs/banao/" },
    { label: "Vikaas", icon: File, path: "/docs/vikaas/" },
    { label: "Vidya", icon: File, path: "/docs/vidya/" },
  ] },
  { label: "My Space", items: [
    { label: "Apply Leave", icon: Calendar, path: "/leave/apply/" },
    // Payslips, bank details and expense claims are tabs of one screen, so one entry.
    { label: "My Pay & Claims", icon: FileText, path: "/payslips/" },
    { label: "Integrations", icon: Mail, path: "/integrations/", alert: "calendarReconnect" },
    { label: "AXON API", icon: KeyRound, path: "/axon-api/" },
  ] },
  // What the AI helpers propose, waiting for a person's yes or no.
  { label: "AI helpers", items: [
    { label: "Suggested Tasks", icon: Inbox, path: "/proposals/" },
  ] },
  { label: "Company", when: runsCompany, items: [
    { label: "Admin Panel", icon: UserCog, path: "/company-admin/", alert: "integrationsDown" },
  ] },
];

// Opened by an auditor engagement or by being platform staff, never by a page grant, so they are not in the sections
// above and not in LABEL_TO_PAGE.
const AUDIT_SECTION = { label: "Audit", items: [{ label: "Audit", icon: KeyRound, path: "/audit/" }] };
const PLATFORM_SECTION = { label: "Platform", items: [{ label: "Companies", icon: KeyRound, path: "/platform/" }] };

// Pages that have a title but no menu entry of their own.
const UNLISTED_PAGES = [{ label: "Notifications", path: "/notifications/" }];

const allEntries = [...NAV_SECTIONS, AUDIT_SECTION, PLATFORM_SECTION].flatMap((section) => section.items).concat(UNLISTED_PAGES);

// The entries this person sees, section headings included, as a flat list for the sidebar.
export function buildNav(me, flags = {}) {
  const sections = me?.auditorOnly ? [AUDIT_SECTION] : [
    ...NAV_SECTIONS.filter((section) => !section.when || section.when(me)),
    ...(me?.isAuditor ? [AUDIT_SECTION] : []),
    ...(me?.user?.isOperator ? [PLATFORM_SECTION] : []),
  ];
  const nav = [];
  for (const section of sections) {
    const items = me?.auditorOnly || section === AUDIT_SECTION || section === PLATFORM_SECTION
      ? section.items
      : section.items.filter((item) => canSeeLabel(me, item.label));
    if (!items.length) continue;
    if (section.label) nav.push({ isSection: true, label: section.label });
    nav.push(...items.map((item) => ({ ...item, alert: Boolean(flags[item.alert]), urgent: Boolean(flags[item.urgent]) })));
  }
  return nav;
}

// An address belongs to an entry when it is that entry's page or one below it ("/lms/" owns "/lms/42/").
const owns = (entryPath, activePath) => {
  const base = entryPath.replace(/\/$/, "");
  return activePath === entryPath || activePath === base || activePath.startsWith(base + "/");
};

// The entry that owns this address: the most specific one, so "/lms/analytics/" lights Sales Pipeline, not Sales CRM too.
export function entryFor(activePath, entries = allEntries) {
  return entries
    .filter((entry) => entry.path && owns(entry.path, activePath) && !(entry.path === "/home/" && activePath !== "/home/"))
    .sort((a, b) => b.path.length - a.path.length)[0];
}

// The menu section a page sits under ("People & HR"), for the top bar. Pages outside the menu sections have none.
export function sectionFor(activePath, nav) {
  const entry = entryFor(activePath, nav.filter((item) => !item.isSection));
  const index = entry ? nav.indexOf(entry) : -1;
  for (let i = index; i >= 0; i -= 1) if (nav[i].isSection) return nav[i].label;
  return null;
}

export function pageTitle(activePath) {
  if (activePath.startsWith("/profile")) return "Profile";
  return entryFor(activePath)?.label || "Home";
}
